import { useApolloClient } from "@apollo/client"
import { useEffect } from "react"
import {
  HomeRegistrationFragment,
  HomeRegistrationsDocument,
  HomeRegistrationsQueryResult
} from "@pathwright/graphql-client/src/generated"
import { usePositioningContext } from "../../../positioning/PositioningContext"
import { useHomeRegistrationVars } from "../../registration/HomeRegistrationQueryVars"

// Handles positioning and inserting a copied path's registration into the user's Home Screen
// registrations list.
function useOnPathCopied(
  copierRegistration?: HomeRegistrationFragment | null,
  cleanup?: () => void
) {
  const client = useApolloClient()
  // We'll explicitly position the copied path to the top of the HOME_SCREEN once copied.
  const {
    positionedObjects,
    updatePositionedObject,
    getPositionedObjectFragment
  } = usePositioningContext()
  const homeRegistrationsVariables = useHomeRegistrationVars()

  // Once registration exists (link successfully copied), we handle positioning it
  // and moving it out and into the Home registrations list. Necessary to update cache
  // since user may perform actions on it (like set aside).
  useEffect(() => {
    async function insertRegistrationIntoCache() {
      if (copierRegistration) {
        // Move registration to the top of the HOME_SCREEN positioning context.
        // But only if there are positioned objects already (well, the positioned objects
        // don't necessarily have a position).
        // NOTE: we do this check as a slight hack as the home screen migration mutation to
        // position all the user's registrations also runs the first time the user has at least
        // one registration on their home screen, and the two mutations can conflict (by possibly
        // trying to position the same registration twice).
        if (positionedObjects.length) {
          await updatePositionedObject({
            item: copierRegistration,
            index: 0,
            getList: (items) => [...items, copierRegistration]
          })
        }

        // Cleanup any state related to copying the path (e.g. clipboard).
        cleanup?.()

        // Add registration to registrations list.
        {
          const query = HomeRegistrationsDocument
          const variables = homeRegistrationsVariables
          // Necessary to read the registration from cache since it now has a position
          // (while the stale registration in scope does not).
          const positionedRegistration =
            client.readFragment(
              getPositionedObjectFragment(copierRegistration.id)
            ) || copierRegistration

          if (positionedRegistration) {
            const data = client.readQuery({
              query,
              variables
            }) as HomeRegistrationsQueryResult["data"]

            const nextData: HomeRegistrationsQueryResult["data"] = {
              ...data,
              me: {
                ...data?.me!,
                registrations: {
                  ...data?.me?.registrations,
                  edges: [
                    {
                      node: positionedRegistration,
                      __typename: "RegistrationEdge"
                    },
                    ...(data?.me?.registrations?.edges || [])
                  ]
                }
              }
            }

            client.writeQuery({
              query,
              variables,
              data: nextData
            })
          }
        }
      }
    }

    insertRegistrationIntoCache()
  }, [!!copierRegistration])
}

export default useOnPathCopied
